/**
 * Exact half-up decimal rounding for money (and any other decimal quantity).
 *
 * Why: `Math.round(n * 100) / 100` rounds the *binary* float, not the decimal
 * amount it stands for. An exact half-cent such as 235.495 is stored as
 * 235.494999…, so the float rounds DOWN and the worker is underpaid 1c. The
 * evaluation trace (award-engines/evaluation-trace.ts) prices bands in exact
 * integer arithmetic with half-up, so the engine and the trace disagreed on
 * exactly these amounts.
 *
 * Semantic (binding for every award engine and payroll wage helper):
 *  1. The float is first read as its nearest 15-significant-digit decimal
 *     (`toPrecision(15)`). Every award input (rates ≤ 4 dp, hours ≤ 4 dp,
 *     multipliers ≤ 3 dp) produces an exact decimal well inside 15 significant
 *     digits, so this recovers the intended decimal and absorbs the few-ulp
 *     residue of float multiplication and summation (525.3349999999999 is read
 *     as 525.335).
 *  2. That decimal is rounded half-up on its digits — 235.495 → 235.50,
 *     1.005 → 1.01, 0.285 → 0.29 — matching the trace's BigInt
 *     `(x + D/2) / D` half-up for every non-negative amount (the trace's whole
 *     domain).
 *  3. Negatives round half AWAY from zero (symmetric): roundMoney(-x) ===
 *     -roundMoney(x), so a reversal or correction of a positive amount is its
 *     exact negation. (Math.round rounds -0.005 to -0.00; this rounds to -0.01.)
 *  4. An amount whose shortest decimal form already has no more than the
 *     requested places is returned unchanged (cent-rounded money is idempotent
 *     however large). Exponent-form inputs (5e-7, 1.2e21) are expanded, never
 *     short-cut.
 *  5. Non-finite input is returned unchanged; -0 is normalised to 0. Supported
 *     domain: |value| × 10^places ≤ Number.MAX_SAFE_INTEGER; outside it the
 *     value is returned unchanged (moneyToCents falls back to Math.round).
 */

const SIGNIFICANT_DIGITS = 15;
const MAX_PLACES = 6;

/** Expand a JS number string ("1.5e-7", "1.2e+21", "525.335") to plain decimal digits. */
function plainDecimal(text: string): { intPart: string; fracPart: string } {
  const [mantissa, exponentText] = text.split('e');
  const exponent = exponentText === undefined ? 0 : Number(exponentText);
  const [mantInt, mantFrac = ''] = mantissa!.split('.');
  const digits = `${mantInt}${mantFrac}`;
  const point = mantInt!.length + exponent;
  if (point <= 0) return { intPart: '0', fracPart: `${'0'.repeat(-point)}${digits}` };
  if (point >= digits.length)
    return { intPart: `${digits}${'0'.repeat(point - digits.length)}`, fracPart: '' };
  return { intPart: digits.slice(0, point), fracPart: digits.slice(point) };
}

function roundToScaledInteger(value: number, places: number): bigint | null {
  const abs = Math.abs(value);
  // The shortest round-trip decimal is exact for the stored double. When it
  // already has no more than `places` decimals the amount is ALREADY at the
  // target precision and must come back unchanged (12345678901234.56 stays
  // put; a 15-significant-digit read would move it 4c).
  const shortest = plainDecimal(String(abs));
  // Otherwise read the nearest 15-significant-digit decimal, which recovers the
  // intended decimal behind float residue (525.3349999999999 → 525.335).
  const { intPart, fracPart } =
    shortest.fracPart.length <= places
      ? shortest
      : plainDecimal(abs.toPrecision(SIGNIFICANT_DIGITS));
  const digits = `${fracPart}${'0'.repeat(places + 1)}`.slice(0, places + 1);
  const kept = BigInt(`${intPart}${digits.slice(0, places)}`);
  const roundUp = digits.charCodeAt(places) - 48 >= 5;
  const scaled = roundUp ? kept + 1n : kept;
  // Supported domain: the scaled integer must be exactly representable.
  return scaled > BigInt(Number.MAX_SAFE_INTEGER) ? null : scaled;
}

/** Round `value` to `places` (0–6) decimal places, exact half-up (away from zero). */
export function roundDecimalHalfUp(value: number, places: number): number {
  if (!Number.isInteger(places) || places < 0 || places > MAX_PLACES) {
    throw new RangeError(`roundDecimalHalfUp places must be an integer 0–${MAX_PLACES}`);
  }
  if (!Number.isFinite(value) || value === 0) return value === 0 ? 0 : value;
  const scaled = roundToScaledInteger(value, places);
  if (scaled === null) return value;
  const magnitude = Number(scaled) / 10 ** places;
  if (magnitude === 0) return 0;
  return value < 0 ? -magnitude : magnitude;
}

/** Round a dollar amount to whole cents, exact half-up (away from zero). */
export function roundMoney(value: number): number {
  return roundDecimalHalfUp(value, 2);
}

/** Convert a dollar amount to integer cents, exact half-up (away from zero). */
export function moneyToCents(value: number): number {
  if (!Number.isFinite(value) || value === 0) return value === 0 ? 0 : value;
  const scaled = roundToScaledInteger(value, 2);
  if (scaled === null) return Math.round(value * 100);
  const cents = Number(scaled);
  if (cents === 0) return 0;
  return value < 0 ? -cents : cents;
}

/**
 * Gross-from-components rule (binding for every award engine): each reported
 * money component (basePay, penaltyPay, shiftLoadingPay, overtimePay,
 * allowancesPay) is rounded to cents on its own with roundMoney, and the gross
 * is the exact integer-cent SUM of those rounded components — never a separate
 * rounding of the unrounded total. Reported components therefore always sum
 * exactly to the reported gross (e.g. 289.875 + 289.875 → 289.88 + 289.88 =
 * 579.76, not 579.75). This matches the evaluation trace, which sums
 * independently rounded band cents and compares them with the cent-sum of the
 * time components.
 */
export function sumMoney(...amounts: readonly number[]): number {
  let cents = 0;
  for (const amount of amounts) {
    if (!Number.isFinite(amount)) return amounts.reduce((sum, value) => sum + value, 0);
    cents += moneyToCents(amount);
  }
  return cents === 0 ? 0 : cents / 100;
}

/**
 * Overtime substitution back-out, per engine rounding unit (Astra RND-04/RND-07,
 * 8 Oct 2026).
 *
 * An engine prices each ordinary band as its own rounding unit (weekday base,
 * Saturday, Sunday, public holiday: round(hours × rate) each) and may add other,
 * separately rounded money to the same component (e.g. a missed-meal penalty).
 * When hours become overtime, only the units that lost hours change: each such
 * unit's rounded value is replaced by the once-rounded value of what remains,
 * round(unitExact − removalExact). Everything else already in `roundedTotal`
 * is carried through untouched — no inference from a rounded total.
 *
 * `units` must be the exact (unrounded) amounts the engine itself rounded into
 * `roundedTotal`, recorded by the engine at pricing time; never reconstruct them
 * from reported (rounded) band hours.
 */
export function backOutRoundingUnits(
  roundedTotal: number,
  units: ReadonlyArray<{ readonly exact: number; readonly removal: number }>,
): number {
  let total = roundedTotal;
  for (const unit of units) {
    if (!(unit.removal > 0)) continue;

    total = total - roundMoney(unit.exact) + roundMoney(Math.max(0, unit.exact - unit.removal));
  }
  return roundMoney(Math.max(0, total));
}
